/**
 * Rubric AI Assistant Component
 * AI-powered rubric creation and refinement tool
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, Stepper, Step, StepLabel,
  TextField, FormControl, InputLabel, Select, MenuItem, Card,
  CardContent, Divider, Tabs, Tab, Grid, IconButton
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import { GRADE_LEVELS, ASSIGNMENT_TYPES, PAT_TYPES } from '../../types/common';
import { Subject } from '../../types/subject';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

const steps = ['Upload/Analyze', 'Review & Refine', 'View/Edit JSON', 'Test & Validate', 'Save Rubric'];

const RubricAIAssistant: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rubricId = searchParams.get('rubricId');

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Tab selection for Step 0
  const [inputMethod, setInputMethod] = useState<'upload' | 'paste'>('upload');

  // Step 1: Upload & Analyze (File Upload)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assignmentType, setAssignmentType] = useState<string>('general');
  const [patPhase, setPatPhase] = useState<string>('');
  const [gradeLevel, setGradeLevel] = useState<number>(12);
  const [subject, setSubject] = useState<string>('IT');
  const [subjects, setSubjects] = useState<Subject[]>([]);

  // Step 1: Paste Documents (Text Input)
  const [questionPaper, setQuestionPaper] = useState('');
  const [memorandum, setMemorandum] = useState('');
  const [assessmentType, setAssessmentType] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  // Analyzed/Generated Rubric
  const [analyzedRubric, setAnalyzedRubric] = useState<any>(null);

  // Step 2: Refine (Chat Interface)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [refineFeedback, setRefineFeedback] = useState(''); // Keep for backwards compatibility

  // Step 3: JSON Editor
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Step 4: Test
  const [sampleWork, setSampleWork] = useState('');
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [simulationResult, setSimulationResult] = useState<any>(null);

  // Step 4: Save
  const [rubricName, setRubricName] = useState('');
  const [rubricDescription, setRubricDescription] = useState('');

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Load per-school subjects for the dropdown
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const res = await axios.get('/api/subjects/?ordering=name&page_size=1000');
        setSubjects(res.data.results || []);
      } catch (err) {
        console.error('Error loading subjects:', err);
      }
    };
    loadSubjects();
  }, []);

  // Load existing rubric if rubricId is provided
  useEffect(() => {
    if (rubricId) {
      const loadExistingRubric = async () => {
        setLoadingExisting(true);
        try {
          const response = await axios.get(`/api/rubrics/${rubricId}/`);
          const rubric = response.data;

          // Populate fields with existing rubric data
          setRubricName(rubric.name);
          setRubricDescription(rubric.description || '');
          setAnalyzedRubric({
            criteria: rubric.criteria,
            name: rubric.name,
            description: rubric.description
          });

          // Skip to Step 2 (Refine) since we already have a rubric
          setActiveStep(1);
          setSuccess('Existing rubric loaded! You can now refine it using the chat below.');

          // Add welcome message to chat
          const welcomeMessage: ChatMessage = {
            role: 'assistant',
            content: `Hello! I've loaded your rubric "${rubric.name}". I'm here to help you refine it. You can ask me to:\n\n• Modify criterion descriptions or point values\n• Add or remove criteria\n• Adjust the overall structure\n• Clarify any aspects of the rubric\n\nWhat would you like to change?`,
            timestamp: new Date(),
          };
          setChatMessages([welcomeMessage]);
        } catch (err: any) {
          console.error('Error loading rubric:', err);
          setError('Failed to load existing rubric. Please try again.');
        } finally {
          setLoadingExisting(false);
        }
      };

      loadExistingRubric();
    }
  }, [rubricId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setError('Please select a file to analyze');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('assignment_type', assignmentType);
      formData.append('grade_level', gradeLevel.toString());
      formData.append('subject', subject);

      // Include PAT phase if assignment type is 'pat'
      if (assignmentType === 'pat' && patPhase) {
        formData.append('pat_phase', patPhase);
      }

      const response = await axios.post('/api/rubrics/ai-analyze/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setAnalyzedRubric(response.data);
      setRubricName(response.data.name || `${subject} Rubric - Grade ${gradeLevel}`);
      setRubricDescription(response.data.description || '');
      setSuccess('Rubric analyzed successfully!');
      setActiveStep(1);
    } catch (err: any) {
      console.error('Error analyzing rubric:', err);
      setError(err.response?.data?.error || 'Failed to analyze rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFromDocuments = async () => {
    if (!questionPaper.trim() || !memorandum.trim()) {
      setError('Please provide both question paper and memorandum');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/rubrics/generate-from-documents/', {
        question_paper: questionPaper,
        memorandum: memorandum,
        assessment_type: assessmentType || 'General Assessment',
        additional_context: additionalContext
      });

      setAnalyzedRubric(response.data);
      setRubricName(response.data.rubric_name || 'AI Generated Rubric');
      setRubricDescription(response.data.description || '');
      setSuccess('Rubric generated successfully!');
      setActiveStep(1);
    } catch (err: any) {
      console.error('Error generating rubric:', err);
      setError(err.response?.data?.detail || 'Failed to generate rubric');
    } finally {
      setLoading(false);
    }
  };

  // Send chat message for rubric refinement
  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: currentMessage.trim(),
      timestamp: new Date(),
    };

    // Add user message to chat
    setChatMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsSending(true);
    setError(null);

    try {
      // Send conversation history + current rubric to backend
      const response = await axios.post('/api/rubrics/ai-refine/', {
        current_rubric: analyzedRubric.criteria,
        feedback: userMessage.content,
        conversation_history: chatMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
      });

      // Update rubric with refined version
      setAnalyzedRubric((prev: any) => ({
        ...prev,
        criteria: response.data.refined_criteria,
      }));

      // Add AI response to chat
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.data.message || 'Rubric updated successfully!',
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, assistantMessage]);

    } catch (err: any) {
      console.error('Error refining rubric:', err);
      const errorMessage: ChatMessage = {
        role: 'system',
        content: err.response?.data?.error || 'Failed to refine rubric. Please try again.',
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, errorMessage]);
      setError(err.response?.data?.error || 'Failed to refine rubric');
    } finally {
      setIsSending(false);
    }
  };

  // Legacy handler - kept for backwards compatibility
  const handleRefine = async () => {
    if (!refineFeedback.trim()) {
      setError('Please provide refinement feedback');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/rubrics/ai-refine/', {
        current_rubric: analyzedRubric.criteria,
        feedback: refineFeedback,
      });

      setAnalyzedRubric((prev: any) => ({
        ...prev,
        criteria: response.data.refined_criteria,
      }));

      setSuccess('Rubric refined successfully!');
      setRefineFeedback('');
    } catch (err: any) {
      console.error('Error refining rubric:', err);
      setError(err.response?.data?.error || 'Failed to refine rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleSimulate = async () => {
    if (!sampleWork.trim() && !sampleFile) {
      setError('Please provide sample work (text or file)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('rubric', JSON.stringify(analyzedRubric.criteria));

      if (sampleWork.trim()) {
        formData.append('sample_work', sampleWork);
      }

      if (sampleFile) {
        formData.append('sample_file', sampleFile);
      }

      const response = await axios.post('/api/rubrics/ai-simulate/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSimulationResult(response.data);
      setSuccess('Simulation completed!');
    } catch (err: any) {
      console.error('Error simulating marking:', err);
      setError(err.response?.data?.error || 'Failed to simulate marking');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!rubricName.trim()) {
      setError('Please provide a rubric name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: rubricName,
        description: rubricDescription,
        criteria: analyzedRubric.criteria,
        is_fundamental: false,
        marking_preamble: analyzedRubric.marking_preamble || '',
        marking_postamble: analyzedRubric.marking_postamble || '',
      };

      await axios.post('/api/rubrics/', payload);

      setSuccess('Rubric saved successfully! Redirecting...');
      setTimeout(() => navigate('/rubrics'), 2000);
    } catch (err: any) {
      console.error('Error saving rubric:', err);
      setError(err.response?.data?.detail || 'Failed to save rubric');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Create Rubric with AI</Typography>
            <Typography variant="body2" color="textSecondary">
              Choose how you want to create your rubric: upload a file or paste text documents.
            </Typography>

            <Tabs value={inputMethod} onChange={(_, newValue) => setInputMethod(newValue)}>
              <Tab icon={<CloudUploadIcon />} label="Upload File" value="upload" />
              <Tab icon={<DescriptionIcon />} label="Paste Documents" value="paste" />
            </Tabs>

            {inputMethod === 'upload' ? (
              <Stack spacing={3}>
                <Typography variant="body2" color="textSecondary">
                  Upload an image, PDF, or text file containing your marking guidelines. Our AI will extract and structure the rubric.
                </Typography>

                <Box>
                  <input
                    type="file"
                    id="rubric-upload"
                    accept=".pdf,.png,.jpg,.jpeg,.txt"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="rubric-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ p: 2 }}
                    >
                      {selectedFile ? selectedFile.name : 'Choose File (PDF, Image, or Text)'}
                    </Button>
                  </label>
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Assignment Type</InputLabel>
                    <Select
                      value={assignmentType}
                      onChange={(e) => setAssignmentType(e.target.value)}
                      label="Assignment Type"
                    >
                      {ASSIGNMENT_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {assignmentType === 'pat' && (
                    <FormControl fullWidth>
                      <InputLabel>PAT Phase</InputLabel>
                      <Select
                        value={patPhase}
                        onChange={(e) => setPatPhase(e.target.value)}
                        label="PAT Phase"
                      >
                        {PAT_TYPES.map((phase) => (
                          <MenuItem key={phase.value} value={phase.value}>{phase.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl fullWidth>
                    <InputLabel>Grade Level</InputLabel>
                    <Select
                      value={gradeLevel}
                      onChange={(e) => setGradeLevel(Number(e.target.value))}
                      label="Grade Level"
                    >
                      {GRADE_LEVELS.map((grade) => (
                        <MenuItem key={grade.value} value={grade.value}>{grade.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Subject</InputLabel>
                    <Select
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      label="Subject"
                    >
                      {subjects.map((subj) => (
                        <MenuItem key={subj.id} value={subj.name}>{subj.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Button
                  variant="contained"
                  onClick={handleAnalyze}
                  disabled={!selectedFile || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                  size="large"
                >
                  {loading ? 'Analyzing...' : 'Analyze with AI'}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Typography variant="body2" color="textSecondary">
                  Paste your question paper and memorandum below. The AI will analyze them and create a detailed rubric.
                </Typography>

                <TextField
                  fullWidth
                  required
                  label="Question Paper"
                  placeholder="Paste the full question paper text here..."
                  value={questionPaper}
                  onChange={(e) => setQuestionPaper(e.target.value)}
                  multiline
                  rows={8}
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  required
                  label="Memorandum (Marking Guide)"
                  placeholder="Paste the memorandum/marking guide text here..."
                  value={memorandum}
                  onChange={(e) => setMemorandum(e.target.value)}
                  multiline
                  rows={8}
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Assessment Type"
                  placeholder="e.g., GR9, Grade 10 Exam, PAT Phase 1"
                  value={assessmentType}
                  onChange={(e) => setAssessmentType(e.target.value)}
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Additional Context (Optional)"
                  placeholder="Any additional information to help the AI..."
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  multiline
                  rows={2}
                  disabled={loading}
                />

                <Button
                  variant="contained"
                  onClick={handleGenerateFromDocuments}
                  disabled={!questionPaper.trim() || !memorandum.trim() || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                  size="large"
                >
                  {loading ? 'Generating...' : 'Generate Rubric with AI'}
                </Button>
              </Stack>
            )}
          </Stack>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Review & Refine Rubric</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Chat with AI to refine your rubric. Ask for changes, additions, or clarifications.
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Left: Rubric Preview */}
              <Box sx={{ flex: { xs: '1', md: '0 0 40%' } }}>
                <Card sx={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Current Rubric
                    </Typography>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                      {analyzedRubric?.rubric_name || analyzedRubric?.name || 'Untitled Rubric'}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {analyzedRubric ? (
                      <Box sx={{ mt: 2 }}>
                        {Object.entries(analyzedRubric.criteria || {}).map(([key, criterion]: [string, any]) => (
                          <Box key={key} sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {criterion.name || key}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Max Points: {criterion.max_points || 0}
                            </Typography>
                            {criterion.description && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {criterion.description}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                        No rubric loaded
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* Right: Chat Interface */}
              <Box sx={{ flex: { xs: '1', md: '0 0 60%' } }}>
                <Card sx={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                  {/* Chat Messages */}
                  <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                    {chatMessages.length === 0 ? (
                      <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Start a conversation to refine your rubric
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Try: "Add more detail to the first criterion" or "Increase total points to 100"
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {chatMessages.map((msg, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <Paper
                              sx={{
                                p: 1.5,
                                maxWidth: '80%',
                                bgcolor: msg.role === 'user'
                                  ? 'primary.main'
                                  : msg.role === 'system'
                                  ? 'error.light'
                                  : 'grey.100',
                                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                {msg.role === 'user' ? 'You' : msg.role === 'assistant' ? 'AI Assistant' : 'System'}
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {msg.content}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                {msg.timestamp.toLocaleTimeString()}
                              </Typography>
                            </Paper>
                          </Box>
                        ))}
                        <div ref={chatEndRef} />
                      </Stack>
                    )}
                  </CardContent>

                  {/* Chat Input */}
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your message..."
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={isSending}
                        multiline
                        maxRows={3}
                      />
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!currentMessage.trim() || isSending}
                      >
                        {isSending ? <CircularProgress size={24} /> : <SendIcon />}
                      </IconButton>
                    </Stack>
                  </Box>
                </Card>
              </Box>
            </Box>

            {/* Navigation Buttons */}
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(0)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(2)}
              >
                Continue to Testing
              </Button>
            </Stack>
          </Box>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Test & Validate</Typography>
            <Typography variant="body2" color="textSecondary">
              Test your rubric by providing sample student work. See how the AI would mark it.
            </Typography>

            <TextField
              label="Sample Student Work (Text)"
              placeholder="Paste sample student work here..."
              value={sampleWork}
              onChange={(e) => setSampleWork(e.target.value)}
              multiline
              rows={6}
              fullWidth
            />

            <Box>
              <Typography variant="body2" gutterBottom>
                Or upload a file:
              </Typography>
              <input
                type="file"
                id="sample-upload"
                accept=".pdf,.png,.jpg,.jpeg,.txt"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setSampleFile(e.target.files[0]);
                  }
                }}
                style={{ display: 'none' }}
              />
              <label htmlFor="sample-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  {sampleFile ? sampleFile.name : 'Upload Sample Work'}
                </Button>
              </label>
            </Box>

            <Button
              variant="outlined"
              onClick={handleSimulate}
              disabled={(!sampleWork.trim() && !sampleFile) || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            >
              {loading ? 'Simulating...' : 'Simulate Marking'}
            </Button>

            {simulationResult && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Simulation Results:
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                    {JSON.stringify(simulationResult, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(1)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(3)}
              >
                Continue to Save
              </Button>
            </Stack>
          </Stack>
        );

      case 3:
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Save Rubric</Typography>
            <Typography variant="body2" color="textSecondary">
              Finalize your rubric by providing a name and description.
            </Typography>

            <TextField
              fullWidth
              required
              label="Rubric Name"
              value={rubricName}
              onChange={(e) => setRubricName(e.target.value)}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={rubricDescription}
              onChange={(e) => setRubricDescription(e.target.value)}
              multiline
              rows={3}
              disabled={loading}
            />

            {analyzedRubric && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Final Rubric Preview:
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" gutterBottom>
                    <strong>Name:</strong> {rubricName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Description:</strong> {rubricDescription || 'None'}
                  </Typography>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em', maxHeight: '300px', overflow: 'auto' }}>
                    {JSON.stringify(analyzedRubric.criteria, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(2)}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!rubricName.trim() || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Saving...' : 'Save Rubric'}
              </Button>
            </Stack>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Home
        </MuiLink>
        <Typography color="text.primary">Rubric AI Assistant</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>Rubric AI Assistant</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Create professional rubrics with AI assistance. Upload files or paste documents to get started.
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </Paper>
    </Box>
  );
};

export default RubricAIAssistant;
